/**
 * m3 echo Frontend - Eigene Domains für den Zitat-Anteil bestätigen
 *
 * Der Homepage-Vorschlag wird getrennt angezeigt und nie automatisch bestätigt;
 * erst "Bestätigen" schreibt die Konfiguration (mit erwarteter Revision).
 */

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCitationDomains, useUpdateCitationDomains } from '../hooks/useCitations';
import { apiErrorStatus } from '../utils/apiErrorStatus';
import type { CitationDomainEntry } from '../types/api';

interface Props {
  open: boolean;
  projectId: number;
  onClose: () => void;
}

const emptyEntry = (kind: 'primary' | 'alias'): CitationDomainEntry => ({
  domain: '', kind, match: 'registrable_domain',
});

/**
 * Aus einer eingefügten Adresse (https://www.example.de/seite) nur den Hostnamen übernehmen.
 * Adressen mit Zugangsdaten oder Port bleiben unverändert, damit der Server sie ablehnt.
 */
const hostFromInput = (raw: string): string => {
  const value = raw.trim();
  const withScheme = /^https?:\/\//i.test(value) ? value : /^[^\s/?#@:]+[/?#]/.test(value) ? `https://${value}` : null;
  if (!withScheme) return value;
  try {
    const url = new URL(withScheme);
    if (url.username || url.password || url.port) return value;
    return url.hostname;
  } catch {
    return value;
  }
};

const errorDetail = (error: unknown): string | null => {
  // Der Interceptor in services/api.ts lehnt mit {message, code, originalError} ab.
  const e = error as { response?: { data?: { detail?: unknown } }; originalError?: { response?: { data?: { detail?: unknown } } } };
  const detail = e?.response?.data?.detail ?? e?.originalError?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d?.msg === 'string' ? d.msg : '')).filter(Boolean).join(' ') || null;
  }
  return null;
};

const CitationDomainsDialog: React.FC<Props> = ({ open, projectId, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data, isLoading, isError, refetch } = useCitationDomains(projectId, open);
  const mutation = useUpdateCitationDomains(projectId);
  const [entries, setEntries] = useState<CitationDomainEntry[]>([]);
  const [message, setMessage] = useState<{ severity: 'error' | 'warning'; text: string } | null>(null);

  // Formular folgt der geladenen Konfiguration (auch nach Konflikt-Reload).
  const revision = data?.revision ?? null;
  useEffect(() => {
    if (!data) return;
    const current = data.config?.domains ?? [];
    setEntries(current.length > 0 ? current.map((d) => ({ ...d })) : [emptyEntry('primary')]);
  }, [data, revision]);

  const suggestion = data?.homepage_suggestion as CitationDomainEntry | null | undefined;
  const suggestionAlreadyListed = !!suggestion && entries.some((e) => e.domain.trim().toLowerCase() === suggestion.domain);

  const update = (index: number, patch: Partial<CitationDomainEntry>) =>
    setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));

  const handleSave = async () => {
    if (!data) return;
    setMessage(null);
    const domains = entries
      .map((e) => ({ ...e, domain: hostFromInput(e.domain) }))
      .filter((e) => e.domain)
      .map((e) => ({ domain: e.domain, kind: e.kind, match: e.match }));
    if (domains.length === 0) {
      setMessage({ severity: 'error', text: 'Bitte mindestens eine Domain angeben.' });
      return;
    }
    try {
      await mutation.mutateAsync({ expected_revision: data.revision, domains });
      await refetch();
      onClose();
    } catch (error) {
      if (apiErrorStatus(error) === 409) {
        setMessage({
          severity: 'warning',
          text: 'Die Domainkonfiguration wurde zwischenzeitlich geändert. Die aktuelle Konfiguration wurde neu geladen; bitte Eingaben prüfen und erneut bestätigen.',
        });
        await refetch();
      } else {
        setMessage({ severity: 'error', text: errorDetail(error) || 'Die Domains konnten nicht gespeichert werden.' });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Eigene Domain bestätigen</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Der Zitat-Anteil zählt nur Quellen dieser Domains. Hostnamen ohne Pfad, z. B. beispiel-energie.de.
        </Typography>
        {isLoading && <Typography variant="body2">Wird geladen …</Typography>}
        {isError && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => refetch()}>Wiederholen</Button>}>
            Die Domainkonfiguration konnte nicht geladen werden.
          </Alert>
        )}
        {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}
        {data && (
          <>
            {suggestion?.domain && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  suggestionAlreadyListed ? undefined : (
                    <Button color="inherit" size="small" onClick={() => setEntries((prev) => [...prev.filter((e) => e.domain.trim()), { ...suggestion }])}>
                      In Liste übernehmen
                    </Button>
                  )
                }
              >
                Vorschlag aus der Homepage: <strong>{suggestion.domain}</strong> (noch nicht bestätigt)
              </Alert>
            )}
            {entries.map((entry, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField
                  label="Domain"
                  size="small"
                  value={entry.domain}
                  onChange={(e) => update(index, { domain: e.target.value })}
                  onBlur={(e) => update(index, { domain: hostFromInput(e.target.value) })}
                  sx={{ flex: '1 1 200px' }}
                  inputProps={{ 'aria-label': `Domain ${index + 1}` }}
                />
                <TextField
                  select
                  size="small"
                  label="Art"
                  value={entry.kind}
                  onChange={(e) => update(index, { kind: e.target.value as 'primary' | 'alias' })}
                  sx={{ width: 130 }}
                >
                  <MenuItem value="primary">Hauptdomain</MenuItem>
                  <MenuItem value="alias">Alias</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Abgleich"
                  value={entry.match}
                  onChange={(e) => update(index, { match: e.target.value as CitationDomainEntry['match'] })}
                  sx={{ width: 170 }}
                >
                  <MenuItem value="registrable_domain">Domain inkl. Subdomains</MenuItem>
                  <MenuItem value="exact_host">Nur dieser Host</MenuItem>
                </TextField>
                <IconButton
                  aria-label={`Domain ${index + 1} entfernen`}
                  disabled={entries.length <= 1}
                  onClick={() => setEntries((prev) => prev.filter((_, i) => i !== index))}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button size="small" onClick={() => setEntries((prev) => [...prev, emptyEntry('alias')])}>
              Weitere Domain
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={handleSave} disabled={!data || mutation.isPending}>
          Bestätigen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CitationDomainsDialog;
